'use client';

import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import Cookies from 'js-cookie';
import ReCAPTCHA from 'react-google-recaptcha';
import { toast } from 'sonner';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';

import { FormHead } from '@/components/auth/components/form-head';
import { Field, Form } from '@/components/hook-form';
import { Iconify } from '@/components/iconify';
import { useAppControlPublic } from '@/hooks/use-app-control';
import { useBoolean } from '@/hooks/use-boolean';
import { GQLMutation, GQLQuery } from '@/lib/client';
import {
  USER_AC_RECOVER,
  USER_AC_RESET,
  USER_CHECK_CAPTCHA,
  USER_CREATE_SELF,
  USER_LOGIN,
  USER_LOGOUT,
  USER_UN_LOCK,
} from '@/lib/mutations/user.mutation';
import { Q_SESSION_SELF } from '@/lib/queries/session.query';
import { sourceImage } from '@/lib/server';
import { useTranslate } from '@/locales/use-locales';
import { validatePhoneNumber } from '@/utils/helper';

import type { AuthView } from './unified-auth-view';
import { SignUpTerms } from '@/components/auth/components/sign-up-terms';
import { GoogleAuthButton } from './google-auth-button';

type FormProps = {
  onChangeView: (view: AuthView) => void;
};

const normalizePhone = (phone: string) => phone.replace(/\D/g, '');
const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

type SignInValues = { email: string; phone: string; password: string };
type SignUpValues = {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
};
type RecoverValues = { email: string };
type ResetValues = { password: string; confirmation: string };
type UnlockValues = { password: string };

function ViewLink({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <Link
      component="button"
      type="button"
      variant="subtitle2"
      onClick={onClick}
      sx={{ verticalAlign: 'baseline' }}
    >
      {children}
    </Link>
  );
}

function PasswordField({ name, label }: { name: string; label: string }) {
  const password = useBoolean();
  const { t } = useTranslate();

  return (
    <Field.Text
      name={name}
      label={label}
      type={password.value ? 'text' : 'password'}
      InputLabelProps={{ shrink: true }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={password.onToggle}
              edge="end"
              aria-label={
                password.value ? t('auth.login.hidePassword') : t('auth.login.showPassword')
              }
            >
              <Iconify icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}

function useCaptcha() {
  const { t } = useTranslate();
  const ref = useRef<ReCAPTCHA>(null);
  const [verified, setVerified] = useState(false);
  const { value: enabled } = useAppControlPublic<boolean>('auth.captchaEnabled', false);
  const { action: check, loading } = GQLMutation({
    mutation: USER_CHECK_CAPTCHA,
    resolver: 'userCheckCaptcha',
    toastmsg: false,
    callback: () => setVerified(true),
  });

  const verify = (token: string | null) => {
    setVerified(false);
    if (token) check({ variables: { input: { captchaToken: token } } });
  };

  const requireVerification = () => {
    if (!enabled || verified) return true;
    toast.error(t('auth.register.captchaError'), { duration: 5000 });
    return false;
  };

  const element = enabled ? (
    <Box display="flex" justifyContent="center" overflow="hidden">
      <ReCAPTCHA
        ref={ref}
        sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ''}
        onChange={verify}
        onExpired={() => setVerified(false)}
      />
    </Box>
  ) : null;

  return { element, loading, requireVerification, verified };
}

export function SignInForm({ onChangeView }: FormProps) {
  const { t } = useTranslate();
  const [mode, setMode] = useState<'email' | 'phone'>('email');
  const methods = useForm<SignInValues>({
    defaultValues: { email: '', phone: '', password: '' },
  });
  const phoneIsValid = validatePhoneNumber(methods.watch('phone').trim());
  const { action: signIn, loading } = GQLMutation({
    mutation: USER_LOGIN,
    resolver: 'login',
    toastmsg: true,
    callback: () => window.location.reload(),
  });

  const submit = methods.handleSubmit((values) => {
    const email = values.email.trim();
    const phone = values.phone.trim();
    const password = values.password.trim();

    methods.clearErrors();
    if (mode === 'email' && !email) {
      methods.setError('email', { message: t('auth.validation.emailRequired') }, { shouldFocus: true });
      return;
    }
    if (mode === 'email' && !isValidEmail(email)) {
      methods.setError('email', { message: t('auth.validation.emailInvalid') }, { shouldFocus: true });
      return;
    }
    if (mode === 'phone' && !validatePhoneNumber(phone)) {
      methods.setError('phone', { message: t('auth.validation.phoneInternational') }, { shouldFocus: true });
      return;
    }
    if (!password) {
      methods.setError('password', { message: t('auth.validation.passwordRequired') }, { shouldFocus: true });
      return;
    }

    signIn({
      variables: {
        input: {
          keyPublic: mode === 'email' ? email : normalizePhone(phone),
          keyPrivate: password,
        },
      },
    });
  });

  return (
    <>
      <FormHead
        title={t('auth.login.title')}
        description={
          <>
            {t('auth.login.noAccount')}{' '}
            <ViewLink onClick={() => onChangeView('sign-up')}>
              {t('auth.login.registerNow')}
            </ViewLink>
          </>
        }
        sx={{ textAlign: { xs: 'center', md: 'left' } }}
      />

      <GoogleAuthButton />
      <Box display="flex" alignItems="center" gap={2} sx={{ my: 3 }}>
        <Box sx={{ height: 1, flex: 1, bgcolor: 'divider' }} />
        <Typography variant="body2" color="text.secondary">
          {t('auth.google.or')}
        </Typography>
        <Box sx={{ height: 1, flex: 1, bgcolor: 'divider' }} />
      </Box>

      <Tabs
        value={mode}
        onChange={(_, value) => {
          methods.clearErrors();
          setMode(value);
        }}
        variant="fullWidth"
        sx={{ mb: 3 }}
      >
        <Tab value="email" label={t('auth.login.emailTab')} />
        <Tab value="phone" label={t('auth.login.phoneTab')} />
      </Tabs>

      <Form methods={methods} onSubmit={submit}>
        <Box gap={3} display="flex" flexDirection="column">
          {mode === 'email' ? (
            <Field.Text
              name="email"
              label={t('auth.login.emailLabel')}
              InputLabelProps={{ shrink: true }}
            />
          ) : (
            <Field.Phone name="phone" label={t('auth.register.phone')} country="KE" />
          )}

          <Box gap={1.5} display="flex" flexDirection="column">
            <Link
              component="button"
              type="button"
              variant="body2"
              color="inherit"
              onClick={() => onChangeView('recover')}
              sx={{ alignSelf: 'flex-end' }}
            >
              {t('auth.login.forgotPassword')}
            </Link>
            <PasswordField name="password" label={t('auth.login.passwordLabel')} />
          </Box>
          <LoadingButton
            fullWidth
            color="inherit"
            size="large"
            type="submit"
            variant="contained"
            loading={loading}
            disabled={mode === 'phone' && !phoneIsValid}
          >
            {t('auth.login.loginButton')}
          </LoadingButton>
        </Box>
      </Form>
    </>
  );
}

export function SignUpForm({ onChangeView }: FormProps) {
  const { t } = useTranslate();
  const captcha = useCaptcha();
  const methods = useForm<SignUpValues>({
    defaultValues: { firstName: '', middleName: '', lastName: '', email: '' },
  });
  const { action: signUp, loading } = GQLMutation({
    mutation: USER_CREATE_SELF,
    resolver: 'userCreateSelf',
    toastmsg: true,
    callback: () => {
      Cookies.remove('jobCode');
      onChangeView('sign-in');
    },
  });
  const submit = methods.handleSubmit((values) => {
    const firstName = values.firstName.trim();
    const middleName = values.middleName.trim();
    const lastName = values.lastName.trim();
    const email = values.email.trim();

    methods.clearErrors();
    if (!firstName) {
      methods.setError('firstName', { message: t('auth.validation.firstNameRequired') }, { shouldFocus: true });
      return;
    }
    if (!lastName) {
      methods.setError('lastName', { message: t('auth.validation.lastNameRequired') }, { shouldFocus: true });
      return;
    }
    if (!email) {
      methods.setError('email', { message: t('auth.validation.emailRequired') }, { shouldFocus: true });
      return;
    }
    if (!isValidEmail(email)) {
      methods.setError('email', { message: t('auth.validation.emailInvalid') }, { shouldFocus: true });
      return;
    }
    // if (!captcha.requireVerification()) return;

    const name = [firstName, middleName, lastName].filter(Boolean).join(' ');
    signUp({
      variables: {
        input: {
          name,
          email,
          jobCode: Cookies.get('jobCode') || undefined,
        },
      },
    });
  });

  return (
    <>
      <FormHead
        title={t('auth.register.title')}
        description={
          <>
            {t('auth.register.alreadyHaveAccount')}{' '}
            <ViewLink onClick={() => onChangeView('sign-in')}>
              {t('auth.register.loginHere')}
            </ViewLink>
          </>
        }
        sx={{ textAlign: { xs: 'center', md: 'left' } }}
      />

      <GoogleAuthButton />
      <Box display="flex" alignItems="center" gap={2} sx={{ my: 3 }}>
        <Box sx={{ height: 1, flex: 1, bgcolor: 'divider' }} />
        <Typography variant="body2" color="text.secondary">
          {t('auth.google.or')}
        </Typography>
        <Box sx={{ height: 1, flex: 1, bgcolor: 'divider' }} />
      </Box>

      <Form methods={methods} onSubmit={submit}>
        <Box gap={3} display="flex" flexDirection="column">
          <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
            <Field.Text
              name="firstName"
              label={t('auth.register.firstName')}
              InputLabelProps={{ shrink: true }}
            />
            <Field.Text
              name="middleName"
              label={t('auth.register.middleName')}
              InputLabelProps={{ shrink: true }}
            />
            <Field.Text
              name="lastName"
              label={t('auth.register.lastName')}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Field.Text
            name="email"
            label={t('auth.register.email')}
            InputLabelProps={{ shrink: true }}
          />
          {captcha.element}

          <SignUpTerms />
          <LoadingButton
            fullWidth
            color="inherit"
            size="large"
            type="submit"
            variant="contained"
            loading={loading || captcha.loading}
            // disabled={captcha.element ? !captcha.verified : false}
          >
            {t('auth.register.submit')}
          </LoadingButton>
        </Box>
      </Form>
    </>
  );
}

export function RecoverForm({ onChangeView }: FormProps) {
  const { t } = useTranslate();
  const methods = useForm<RecoverValues>({
    defaultValues: { email: '' },
  });
  const { action: recover, loading } = GQLMutation({
    mutation: USER_AC_RECOVER,
    resolver: 'recoverAccount',
    toastmsg: true,
    callback: () => onChangeView('sign-in'),
  });

  const submit = methods.handleSubmit(({ email: inputEmail }) => {
    const email = inputEmail.trim();
    methods.clearErrors();
    if (!email) {
      methods.setError('email', { message: t('auth.validation.emailRequired') }, { shouldFocus: true });
      return;
    }
    if (!isValidEmail(email)) {
      methods.setError('email', { message: t('auth.validation.emailInvalid') }, { shouldFocus: true });
      return;
    }
    recover({ variables: { input: { keyPublic: email } } });
  });

  return (
    <>
      <FormHead
        title={t('auth.recover.title')}
        description={t('auth.recover.description')}
        sx={{ textAlign: { xs: 'center', md: 'left' } }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('auth.recover.instructions')}
      </Typography>
      <Form methods={methods} onSubmit={submit}>
        <Box gap={3} display="flex" flexDirection="column">
          <Field.Text
            name="email"
            label={t('auth.recover.emailLabel')}
            InputLabelProps={{ shrink: true }}
          />
          <LoadingButton
            fullWidth
            color="inherit"
            size="large"
            type="submit"
            variant="contained"
            loading={loading}
          >
            {t('auth.recover.submit')}
          </LoadingButton>
          <Box textAlign="center">
            <ViewLink onClick={() => onChangeView('sign-in')}>
              {t('auth.recover.clickToLogin')}
            </ViewLink>
          </Box>
        </Box>
      </Form>
    </>
  );
}

export function ResetForm({ token, onChangeView }: FormProps & { token?: string }) {
  const { t } = useTranslate();
  const captcha = useCaptcha();
  const methods = useForm<ResetValues>({
    defaultValues: { password: '', confirmation: '' },
  });
  const { action: reset, loading } = GQLMutation({
    mutation: USER_AC_RESET,
    resolver: 'resetPassword',
    toastmsg: true,
    callback: () => onChangeView('sign-in'),
  });
  const submit = methods.handleSubmit((values) => {
    methods.clearErrors();
    if (!token) {
      toast.error(t('auth.reset.invalidLink'));
      return;
    }
    if (values.password.length < 8) {
      methods.setError('password', { message: t('auth.validation.passwordMin') }, { shouldFocus: true });
      return;
    }
    if (!/[a-z]/.test(values.password) || !/[A-Z]/.test(values.password) || !/\d/.test(values.password)) {
      methods.setError('password', { message: t('auth.validation.passwordComplexity') }, { shouldFocus: true });
      return;
    }
    if (!/[^A-Za-z0-9]/.test(values.password)) {
      methods.setError('password', { message: t('auth.validation.passwordSpecial') }, { shouldFocus: true });
      return;
    }
    if (!values.confirmation) {
      methods.setError('confirmation', { message: t('auth.validation.confirmPasswordRequired') }, { shouldFocus: true });
      return;
    }
    if (values.password !== values.confirmation) {
      methods.setError('confirmation', { message: t('auth.validation.passwordMismatch') }, { shouldFocus: true });
      return;
    }
    // if (!captcha.requireVerification()) return;
    reset({
      variables: {
        input: {
          token,
          password: values.password,
          passwordConfirmartion: values.confirmation,
        },
      },
    });
  });

  return (
    <>
      <FormHead
        title={t('auth.reset.title')}
        description={t('auth.reset.description')}
        sx={{ textAlign: { xs: 'center', md: 'left' } }}
      />
      <Box component="ul" sx={{ pl: 3, mb: 4, color: 'text.secondary' }}>
        <li>{t('auth.reset.minimum')}</li>
        <li>{t('auth.reset.lettersAndNumber')}</li>
        <li>{t('auth.reset.specialCharacter')}</li>
      </Box>
      <Form methods={methods} onSubmit={submit}>
        <Box gap={3} display="flex" flexDirection="column">
          <PasswordField name="password" label={t('auth.register.password')} />
          <PasswordField name="confirmation" label={t('auth.register.confirmPassword')} />
          {captcha.element}
          <LoadingButton
            fullWidth
            color="inherit"
            size="large"
            type="submit"
            variant="contained"
            loading={loading || captcha.loading}
            // disabled={!token || (captcha.element ? !captcha.verified : false)}
          >
            {t('auth.reset.save')}
          </LoadingButton>
          <Box textAlign="center">
            <ViewLink onClick={() => onChangeView('sign-in')}>
              {t('auth.recover.backToLogin')}
            </ViewLink>
          </Box>
        </Box>
      </Form>
    </>
  );
}

export function UnlockForm() {
  const { t } = useTranslate();
  const { data: session } = GQLQuery({ query: Q_SESSION_SELF, queryAction: 'sessionSelf' });
  const methods = useForm<UnlockValues>({
    defaultValues: { password: '' },
  });
  const { action: unlock, loading: unlocking } = GQLMutation({
    mutation: USER_UN_LOCK,
    resolver: 'sessionUnlock',
    toastmsg: true,
    callback: () => window.location.reload(),
  });
  const { action: signOut, loading: signingOut } = GQLMutation({
    mutation: USER_LOGOUT,
    resolver: 'logout',
    toastmsg: true,
    callback: () => window.location.reload(),
  });
  const submit = methods.handleSubmit(({ password: inputPassword }) => {
    const password = inputPassword.trim();
    methods.clearErrors();
    if (!password) {
      methods.setError('password', { message: t('auth.validation.passwordRequired') }, { shouldFocus: true });
      return;
    }
    unlock({ variables: { input: { keyPrivate: password } } });
  });

  return (
    <>
      <Box display="flex" justifyContent={{ xs: 'center', md: 'flex-start' }} mb={3}>
        <Avatar
          src={sourceImage(session?.user?.profile?.photo?.fileName)}
          alt={session?.user?.name || ''}
          sx={{ width: 72, height: 72 }}
        />
      </Box>
      <FormHead
        title={session?.user?.name || t('auth.unlock.title')}
        description={
          <>
            <Typography component="span" variant="subtitle2" display="block" color="text.primary">
              {t('auth.unlock.paused')}
            </Typography>
            {t('auth.unlock.description')}
          </>
        }
        sx={{ textAlign: { xs: 'center', md: 'left' } }}
      />
      <Form methods={methods} onSubmit={submit}>
        <Box gap={3} display="flex" flexDirection="column">
          <PasswordField name="password" label={t('auth.login.passwordLabel')} />
          <LoadingButton
            fullWidth
            color="inherit"
            size="large"
            type="submit"
            variant="contained"
            loading={unlocking}
          >
            {t('auth.unlock.continue')}
          </LoadingButton>
          <Button
            fullWidth
            color="inherit"
            size="large"
            variant="outlined"
            disabled={signingOut}
            onClick={() => signOut()}
          >
            {t('auth.unlock.newSession')}
          </Button>
        </Box>
      </Form>
    </>
  );
}
